import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { resolveConfluenceDraftSiblingParent } from '@atlassian/conversation-assistant-utils/resolveConfluenceDraftSiblingParent';

type DuplicateMauiApp = (
	appId: string,
	containerContentId: string,
	contentType?: string,
) => Promise<{ id: string }>;

type RovoConversationContext = {
	conversationId: string;
	messageId: string;
	invocationId: string;
};

type MauiEmbedTargetSpace = {
	id: string;
	key: string;
	parentId?: string;
};

type CreateConfluenceMauiDiagramPasteConfigOptions = {
	contentId: string | undefined;
	contentType?: string;
	duplicateMauiApp?: DuplicateMauiApp;
	siteId: string;
	spaceKey: string;
	userId?: string;
};

const PERMISSION_ERROR_SUBSTRING = 'can not create in space';

class PermissionError extends Error {
	public readonly isPermissionError = true;

	constructor(message: string) {
		super(message);
		this.name = 'PermissionError';
	}
}

/**
 * Returns the error to throw for a failed HTTP response
 */
async function buildErrorFromResponse(message: string, response: Response): Promise<Error> {
	const messageWithStatus = `${message} with status ${response.status}`;
	if (response.status === 400) {
		const rawText = await response.text().catch(() => '');
		if (rawText.toLowerCase().includes(PERMISSION_ERROR_SUBSTRING)) {
			return new PermissionError(messageWithStatus);
		}
	}
	return new Error(messageWithStatus);
}

export function createConfluenceMauiDiagramPasteConfig({
	contentId,
	contentType,
	duplicateMauiApp,
	siteId,
	spaceKey,
	userId,
}: CreateConfluenceMauiDiagramPasteConfigOptions) {
	return {
		getCurrentPageId: () => contentId,
		getCurrentContentType: () => contentType,
		duplicateMauiApp,
		finalizeLocation: {
			currentParentContent: contentId
				? {
						contentType: contentType ?? 'page',
						id: contentId,
						title: '',
					}
				: undefined,
			currentSpace: {
				id: spaceKey,
				key: spaceKey,
				name: spaceKey,
				canCreatePage: false,
			},
			siteId,
			userId,
		},
		finalizeAfterMove: async (
			mauiContentId: string,
			parentPageId: string,
			conversationContext?: RovoConversationContext,
			targetSpace?: MauiEmbedTargetSpace,
		) => {
			const resolvedSpaceKey = targetSpace?.key ?? spaceKey;
			const targetSpaceId = targetSpace?.id;

			// Drafts can't have children, so reparent as a sibling of the
			// draft when possible; fall back to `parentPageId` otherwise.
			let resolvedParentId = targetSpace?.parentId ?? parentPageId;
			if (
				!targetSpace?.parentId &&
				!expVal<boolean>('confluence_maui_finalize_diagram_sibling_draft', 'value', false)
			) {
				try {
					const ancestorId = await resolveConfluenceDraftSiblingParent(parentPageId);
					if (ancestorId) {
						resolvedParentId = ancestorId;
					}
				} catch {
					// Swallow so finalize still proceeds with the original parent.
				}
			}

			// Both finalize endpoints accept the human-readable space key
			// (for example "~70121a2f09606006e4cddb4d8beb963e42aca" for
			// personal spaces) under the `spaceKey` field.
			// 1. When `aifc_finalize_convo_ai` is OFF, call the
			//    monolith finalize endpoint. This handles BOTH the
			//    cross-space reparent AND the recents/finalize bookkeeping.
			//    No frontend GraphQL move is needed.
			if (!fg('aifc_finalize_convo_ai')) {
				const monolithResponse = await fetch(
					`/gateway/api/ex/confluence/${siteId}/wiki/rest/internals/latest/smart-create/finalize`,
					{
						method: 'PUT',
						credentials: 'include',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							id: mauiContentId,
							type: 'whiteboard',
							spaceKey: resolvedSpaceKey,
							parentId: resolvedParentId,
							...(targetSpaceId ? { spaceId: targetSpaceId } : {}),
						}),
					},
				);
				if (!monolithResponse.ok) {
					throw await buildErrorFromResponse('Monolith finalize failed', monolithResponse);
				}
				return;
			}

			// 2. When `aifc_finalize_convo_ai` is ON, call the convo-ai
			//    action endpoint. The convo-ai BE handles BOTH the
			//    cross-space move (with elevated privileges) AND the
			//    finalize.
			//
			//    Conversation context is required — without it we have
			//    no way to identify the action server-side and the BE
			//    move can't be triggered. The banner forwards the `_rc`
			//    payload from the embed URL when present; if it's
			//    missing the link cannot be finalized via convo-ai.
			if (!conversationContext) {
				throw new Error(
					'Cannot finalize: no conversation context found in the embed link. ' +
						'The link may be from an older Rovo Chat session.',
				);
			}

			// The convo-ai BE expects the same shape that the working MAUI
			// embed finalize buttons send (see
			// platform/packages/ai-mate/conversation-assistant/src/ui/staging-area-modal/staging-area/finalize-area/maui-embed-finalize-buttons/index.tsx
			// and `mapConfluenceMessageToActionPayload` in
			// platform/packages/ai-mate/rovo-platform-ui-components/src/ui/confluence-object-finalize-popup/index.tsx).
			// That shape spreads the original `messageAction` (key,
			// invocationId, actionId, displayName, status, toolSource,
			// humanMessageId, confirmationMessage, executedData) and the
			// full `action.data` (document, title, contentId) — all needed
			// for BE validation.
			//
			// Since the URL `_rc` param only carries the action IDs (to
			// avoid bloating the URL with the full document ADF), we fetch
			// the conversation messages here and rebuild the payload with
			// the full action data before POSTing.
			const messagesResponse = await fetch(
				`/gateway/api/assist/rovo/v1/chat/conversation/${conversationContext.conversationId}/messages`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						'x-product': 'confluence',
						'x-experience-id': 'ai-mate',
					},
				},
			);
			if (!messagesResponse.ok) {
				throw new Error(`Failed to fetch conversation messages: ${messagesResponse.status}`);
			}
			const messagesPayload = await messagesResponse.json();
			const messages = Array.isArray(messagesPayload?.items) ? messagesPayload.items : [];

			// Locate the message and the action that originated this
			// finalize (matched by messageId and invocationId).
			let matchedAction: Record<string, unknown> | undefined;
			for (const message of messages) {
				if (message?.id !== conversationContext.messageId) {
					continue;
				}
				const actions = Array.isArray(message?.actions) ? message.actions : [];
				matchedAction = actions.find(
					(action: Record<string, unknown>) =>
						action?.invocationId === conversationContext.invocationId,
				);
				if (matchedAction) {
					break;
				}
			}

			if (!matchedAction) {
				throw new Error(
					'Could not locate the originating action in the Rovo conversation. ' +
						'It may have been deleted or the conversation has expired.',
				);
			}

			const existingData =
				(matchedAction.data as { metadata?: Record<string, unknown> } | undefined) ?? {};
			const existingMetadata = existingData.metadata ?? {};

			const actionResponse = await fetch(
				`/gateway/api/assist/rovo/v1/chat/conversation/${conversationContext.conversationId}/action`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json;charset=UTF-8',
						'x-product': 'confluence',
						'x-experience-id': 'ai-mate',
					},
					body: JSON.stringify({
						context: {
							...matchedAction,
							messageId: conversationContext.messageId,
							status: 'pending',
							resolution: {
								type: 'confirm',
								message: 'finalize',
							},
							data: {
								...existingData,
								metadata: {
									...existingMetadata,
									contentId: mauiContentId,
									parentId: resolvedParentId,
									spaceKey: resolvedSpaceKey,
									...(targetSpaceId ? { spaceId: targetSpaceId } : {}),
								},
							},
						},
					}),
				},
			);
			if (!actionResponse.ok) {
				throw await buildErrorFromResponse('Convo-ai finalize action failed', actionResponse);
			}
		},
		isFinalizePermissionError: (error: Error) =>
			error instanceof PermissionError && error.isPermissionError,
		resolveContentSpaceKey: async (mauiContentId: string) => {
			try {
				const response = await fetch(`/wiki/rest/api/content/${mauiContentId}?expand=space`, {
					credentials: 'include',
				});
				if (!response.ok) {
					return null;
				}
				const data = await response.json();
				return data?.space?.key ?? null;
			} catch {
				return null;
			}
		},
		targetSpaceKey: spaceKey,
	};
}
